import type { PageParam, PageResult, PagedMetaEnvelope, PagedQueryArg } from './paged-types';
import { buildPagedQueryString } from './query-params';

export type UserListItem = {
    id: string;
    seequentUserId: string;
    login: string;
    email: string;
    subscriptionId: string;
    type: string;
    isSubscriptionOwner: boolean;
    state: string;
};

type UserApiItem = {
    id?: string;
    seequentUserId?: string | null;
    login?: string | null;
    email?: string | null;
    type?: string | null;
    isSubscriptionOwner?: boolean;
    state?: string | null;
    subscriptionId?: string | null;
};

type UsersApiResponse = {
    users?: UserApiItem[];
    meta?: PagedMetaEnvelope;
};

type UserApiItemWithId = UserApiItem & { id: string };

const hasId = (user: UserApiItem): user is UserApiItemWithId => typeof user.id === 'string';

const transformUser = (user: UserApiItemWithId): UserListItem => ({
    id: user.id,
    seequentUserId: user.seequentUserId ?? '',
    login: user.login ?? '',
    email: user.email ?? '',
    subscriptionId: user.subscriptionId ?? '',
    type: user.type ?? '',
    isSubscriptionOwner: user.isSubscriptionOwner ?? false,
    state: user.state ?? 'I',
});

export type UsersPageResult = PageResult<UserListItem>;
export type UsersQueryArg = PagedQueryArg;
export type UsersLookupResponse = UserListItem[];

/**
 * Non-paginated users endpoint used as a small lookup for joining `username`
 * onto the support-packages list. Will be removed when the backend embeds
 * user on supportPackage.
 */
export const usersLookupQuery = {
    query: () => ({
        url: '/mp/1/users',
        method: 'get',
    }),
    transformResponse: (response: UsersApiResponse): UserListItem[] => {
        if (!response?.users) return [];
        return response.users.filter(hasId).map(transformUser);
    },
};

export const usersInfiniteQuery = {
    query: ({ queryArg, pageParam }: { queryArg: UsersQueryArg; pageParam: PageParam }) => ({
        url: `/mp/1/users?${buildPagedQueryString({
            offset: pageParam.offset,
            limit: queryArg.limit,
            includeCount: true,
            order: queryArg.order,
            where: queryArg.where,
        })}`,
        method: 'get',
    }),
    transformResponse: (response: UsersApiResponse): UsersPageResult => {
        const rows = (response?.users ?? []).filter(hasId).map(transformUser);
        return {
            rows,
            total: response?.meta?.total ?? rows.length,
            offset: response?.meta?.offset ?? 0,
            limit: response?.meta?.limit ?? rows.length,
        };
    },
    keepUnusedDataFor: 0,
    infiniteQueryOptions: {
        initialPageParam: { offset: 0 },
        getNextPageParam: (
            lastPage: UsersPageResult,
            _allPages: UsersPageResult[],
            lastPageParam: PageParam,
        ): PageParam | undefined => {
            if (lastPage.rows.length === 0) return undefined;
            const loaded = lastPageParam.offset + lastPage.rows.length;
            return loaded < lastPage.total ? { offset: loaded } : undefined;
        },
        getPreviousPageParam: (): PageParam | undefined => undefined,
    },
};
